import { AsyncPipe, CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  QueryList,
  ViewChildren,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { Store } from "@ngxs/store";
import { combineLatest, Observable, of } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { InlineEditComponent } from "../shared/inline-edit/inline-edit.component";
import {
  CitoApplicationModule,
  LearningObjective,
  LearningObjectiveVM,
} from "../shared/model/model";
import {
  CandidateProgressState,
  CandidateProgressStateModel,
  LoadCandidateProgressAction,
  UpdateNameAction,
  UpdateProfileAction,
} from "../shared/store/candidate-progress.state";
import { DashboardState } from "../shared/store/dashboard.state";
import {
  CheckboxModel,
  LearningObjectivesState,
} from "../shared/store/learning-objectives.state";
import { FilterCandidatePlanningAction } from "../shared/store/planning-filter.state";
import {
  PlanningState,
  UpdateProfileInPlanningAction,
} from "../shared/store/planning.state";

export class StateIcon {
  icon: string;
  color: string;
  url: string;
  state: string;
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    AsyncPipe,
    TranslateModule,
    InlineEditComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "app-candidate-progress",
  templateUrl: "candidateprogress.component.html",
  styleUrls: ["candidateprogress.component.scss"],
})
export class CandidateProgressComponent {
  @ViewChildren("name")
  private nameInput: QueryList<ElementRef>;
  citomodules = CitoApplicationModule;
  progressState$: Observable<CandidateProgressStateModel>;
  learningObjectives$: Observable<LearningObjective[]>;
  citoModule$: Observable<CitoApplicationModule>;
  learningroutes$: Observable<CheckboxModel[]>;
  referencelevels$: Observable<CheckboxModel[]>;
  dates = of(new Array<string>());
  candidateName = "";
  domainIcons: any = {
    "meten & meetkunde": "./assets/domainicons/DomainIcons_3.svg",
    verhoudingen: "./assets/domainicons/DomainIcons_2.svg",
    getallen: "./assets/domainicons/DomainIcons_1.svg",
    verbanden: "./assets/domainicons/verbanden.svg",
    "meten & meetkunde_opdracht": "./assets/domainicons/DomainIcons_6.svg",
    verhoudingen_opdracht: "./assets/domainicons/DomainIcons_5.svg",
    getallen_opdracht: "./assets/domainicons/DomainIcons_4.svg",
    verbanden_opdracht: "./assets/domainicons/verbanden_opdracht.svg",
  };
  learningRoute: string;
  learningRouteRadio: number;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private store: Store,
    private ref: ChangeDetectorRef
  ) {
    this.learningRouteRadio = 1;
    this.referencelevels$ = this.store.select(
      PlanningState.referencelevelFilters
    );
    this.learningObjectives$ = this.store.selectOnce(
      LearningObjectivesState.allLearningObjectives
    );
    this.progressState$ = this.route.params.pipe(
      switchMap((params: Params) => {
        const candidateId = params["id"];
        this.store.dispatch(new FilterCandidatePlanningAction(candidateId));
        this.store.dispatch(new LoadCandidateProgressAction(candidateId));

        return combineLatest([
          this.store.select(CandidateProgressState as any),
          this.store.select(DashboardState.citomodule),
        ]).pipe(
          map(
            ([progress, citomodule]: [
              CandidateProgressStateModel,
              CitoApplicationModule
            ]) => {
              if (progress.candidate) {
                if (citomodule === CitoApplicationModule.Rekenen) {
                  this.learningRoute = progress.candidate.learningRoutesList[0];
                } else if (citomodule === CitoApplicationModule.Spelling) {
                  this.learningRoute =
                    progress.candidate.learningRoutesSpellingList[0];
                }
              }
              this.citoModule$ = of(citomodule);
              return progress;
            }
          )
        );
      })
    );
  }

  getBirthDate = (date: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    if (date) {
      return new Date(date).toLocaleDateString("nl-NL", options);
    } else {
      return "";
    }
  };

  getLearningObjective = (
    id: string,
    learningObjectives: LearningObjectiveVM[]
  ) => learningObjectives.find((l) => l.id === id);

  close(id: string) {
    this.store.dispatch(
      new UpdateProfileInPlanningAction(id, [this.learningRoute])
    );
    this.router.navigate(["/plan"], { relativeTo: this.route });
  }

  saveProfile(id: string) {
    this.store.dispatch(new UpdateProfileAction(id, [this.learningRoute]));
  }

  removeProfile(id: string) {
    this.store.dispatch(new UpdateProfileAction(id, [""]));
    this.store.dispatch(new UpdateProfileInPlanningAction(id, [""]));
  }

  onSave(id: string, name: string) {
    this.store.dispatch(new UpdateNameAction(id, name));
  }
}
